import {
  type LiveModelChoice,
  MAX_GROUP_PERSONAS,
  type ModelConsumerResponse,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import {
  DesktopDropdownContent,
  DesktopDropdownItem,
  DesktopDropdownLabel,
} from '@crowdex/ui/components/desktop';
import { DropdownMenu, DropdownMenuTrigger } from '@crowdex/ui/components/dropdown-menu';
import { cn } from '@crowdex/ui/lib/utils';
import {
  AudioLines,
  Check,
  ChevronRight,
  LoaderCircle,
  MessageSquare,
  Mic,
  MicOff,
  PhoneOff,
  ShieldQuestion,
  User,
  UserPlus,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { AppFrame } from '@/features/apps/app-frame';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import type { TurnStep } from './call-api';
import { useCallLevel } from './call-level';
import { useCallSpeech } from './call-speech';
import type { LiveCall, LiveCallStatus } from './use-live-call';

/** The pieces of the live call, shown in its floating panel and, larger, in the Live tab. */

const STATUS: Record<LiveCallStatus, string> = {
  idle: 'Not in a call',
  connecting: 'Connecting…',
  listening: 'Listening',
  speaking: 'Speaking',
  working: 'Working…',
};

export const iconButton =
  'flex size-control-sm items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/** Starting a call, wherever it is offered (the composer, the Live tab): the same waves. */
export const CallStartIcon = AudioLines;

/** A call view's frame does nothing but show: it has no conversation, page or dialog. */
const CALL_VIEW_HANDLERS = {
  ask: () => {
    throw new Error('A call view cannot ask the agent.');
  },
  attach: () => {
    throw new Error('A call view cannot ask the agent.');
  },
  open: () => {
    throw new Error('A call view cannot open other views.');
  },
  closeDialog: () => {
    throw new Error('This view is not a dialog.');
  },
  confirm: () => false,
  selectItem: () => undefined,
  refreshPage: () => undefined,
};

/** The call view the user chose for Live (Settings › Voice), `none`, else the first. */
function useCallView() {
  const callViews = useWorkbench().callViews;
  const [chosen, setChosen] = useState<string | undefined>();
  useEffect(() => {
    let live = true;
    apiFetch<ModelConsumerResponse>('/api/models/consumers/live').then(
      (answer) => live && setChosen((answer.choice as LiveModelChoice | null)?.view),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, []);
  return chosen === 'none'
    ? undefined
    : (callViews.find((view) => view.plugin === chosen) ?? callViews[0]);
}

const NOTHING_SAID: readonly { id: number; text: string }[] = [];

/**
 * The assistant's face: the call view a plugin contributes (an avatar), following the call. `face`
 * zooms on its top centre, where a portrait's face sits, for a view too small to show more.
 */
export function CallView({
  call,
  className,
  face = false,
  member,
}: {
  call: LiveCall;
  className: string;
  face?: boolean;
  /** An assistant of a group call, in its tile: it speaks, prepares an answer or listens. */
  member?: string;
}) {
  const callView = useCallView();
  const callLevel = useCallLevel();
  const callSpoken = useCallSpeech();
  // In a group call, one assistant speaks at a time: the others listen, or think.
  const shown =
    member ??
    (call.group === null
      ? call.speaker
      : (call.groupSpeaker ?? call.groupThinking ?? call.group.members[0]?.id ?? null));
  // Its face is its avatar's; an assistant without one shows the call view's own.
  const avatar =
    (call.group?.members ?? call.assistants).find((entry) => entry.id === shown)?.avatar ?? null;
  const tile = call.group !== null;
  const speaksNow = !tile || call.groupSpeaker === shown;
  const level = speaksNow ? callLevel : 0;
  const spoken = speaksNow ? callSpoken : NOTHING_SAID;
  const state =
    call.status === 'idle'
      ? 'listening'
      : !tile
        ? call.status
        : call.groupSpeaker === shown
          ? 'speaking'
          : call.groupThinking === shown
            ? 'working'
            : call.status === 'connecting'
              ? 'connecting'
              : 'listening';
  if (callView === undefined)
    return (
      <div className={cn('flex shrink-0 items-center justify-center', className)}>
        <span className="flex aspect-square h-1/2 max-h-28 items-center justify-center rounded-full bg-foreground/8 text-muted-foreground">
          <AudioLines aria-hidden="true" className="size-1/2" />
        </span>
      </div>
    );
  return (
    <div className={cn('relative shrink-0 overflow-hidden rounded-lg bg-muted', className)}>
      <div className={face ? 'absolute -top-[62%] -left-[75%] size-[250%]' : 'size-full'}>
        <AppFrame
          call={{
            // Out of a call, it waits as it listens.
            state,
            muted: call.muted || call.status === 'idle',
            level,
            spoken,
            language: navigator.language,
            ...(avatar === null ? {} : { persona: avatar }),
          }}
          container="call"
          handlers={CALL_VIEW_HANDLERS}
          path={callView.path}
          plugin={callView.plugin}
          title={callView.title}
        />
      </div>
    </div>
  );
}

/** A round control of the call: mute, hang up, call. */
const roundButton =
  'flex shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
const roundSize = { md: 'size-11 [&_svg]:size-[18px]', sm: 'size-8 [&_svg]:size-3.5' } as const;
const roundNeutral = 'bg-foreground/8 text-foreground hover:bg-foreground/14';

/** The call's voice, as bars following how loud it speaks. */
function VoiceBars() {
  const level = useCallLevel();
  return (
    <span aria-hidden="true" className="flex h-3 shrink-0 items-center gap-0.5">
      {[0.55, 1, 0.75, 0.9].map((weight) => (
        <span
          className="w-0.5 rounded-full bg-current transition-[height] duration-75"
          key={weight}
          style={{ height: `${Math.round(3 + Math.min(1, level * 1.6) * weight * 9)}px` }}
        />
      ))}
    </span>
  );
}

/** Where the call stands, and what the assistant does now. */
export function CallStatus({ call }: { call: LiveCall }) {
  const muted = call.muted && call.status !== 'connecting' && call.status !== 'idle';
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-muted-foreground text-xs" role="status">
        {call.status === 'connecting' || call.status === 'working' ? (
          <LoaderCircle aria-hidden="true" className="size-3 shrink-0 motion-safe:animate-spin" />
        ) : muted ? (
          <MicOff aria-hidden="true" className="size-3 shrink-0" />
        ) : call.status === 'speaking' ? (
          <VoiceBars />
        ) : (
          <span
            aria-hidden="true"
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              call.status === 'idle' ? 'bg-muted-foreground/50' : 'bg-foreground',
            )}
          />
        )}
        {muted ? 'Microphone muted' : STATUS[call.status]}
      </p>
      {call.status === 'working' && call.activity !== null && (
        <p className="truncate text-muted-foreground/80 text-xs" title={call.activity}>
          {call.activity}
        </p>
      )}
    </div>
  );
}

export function CallError({ call }: { call: LiveCall }) {
  if (call.error === null) return null;
  return (
    <div
      className="flex items-start gap-2 rounded-lg bg-destructive/10 px-2.5 py-2 text-destructive text-xs"
      role="alert"
    >
      <p className="min-w-0 flex-1">{call.error}</p>
      <button
        aria-label="Dismiss"
        className="shrink-0 rounded hover:bg-destructive/10"
        onClick={call.dismissError}
        type="button"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

/** What was said in the call, one line after the other. */
export function CallTranscript({ call, className }: { call: LiveCall; className?: string }) {
  if (call.transcript.length === 0) return null;
  return (
    <ol aria-label="Transcript" className={cn('flex flex-col gap-2.5', className)}>
      {call.transcript.map((line, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: Transcript lines have no id: they are appended, and only the last one changes.
        <li className="flex gap-2 leading-snug" key={`${index}:${line.text}`}>
          {line.role === 'user' ? (
            <User aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <AudioLines aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          )}
          <p className={cn('min-w-0', line.role === 'user' && 'text-muted-foreground')}>
            <span className="sr-only">{line.role === 'user' ? 'You: ' : 'Assistant: '}</span>
            {line.text}
          </p>
        </li>
      ))}
    </ol>
  );
}

/** The steps of the assistant's latest turn. */
export function CallSteps({ call, className }: { call: LiveCall; className?: string }) {
  if (call.steps.length === 0) return null;
  return (
    <ol aria-label="Steps" className={cn('flex flex-col gap-1.5', className)}>
      {call.steps.map((step) => (
        <li className="flex items-center gap-2" key={step.id}>
          <StepIcon state={step.state} />
          <span
            className={cn('truncate', step.state !== 'running' && 'text-muted-foreground')}
            title={step.label}
          >
            {step.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** The conversations the assistant works with: each opens. */
export function CallConversations({ call, className }: { call: LiveCall; className?: string }) {
  const opener = useConversationOpener();
  if (call.followed.length === 0 || opener === null) return null;
  return (
    <ul aria-label="Conversations it works with" className={cn('flex flex-col gap-1', className)}>
      {call.followed.map((entry) => (
        <li key={entry.conversation}>
          <button
            className="group flex min-h-9 w-full items-center gap-2.5 rounded-xl bg-control px-3 py-2 text-left hover:bg-control/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => void opener.openConversation(entry.conversation)}
            title="Open this conversation"
            type="button"
          >
            <MessageSquare aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{entry.label}</span>
            <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** What a turn waits on the user for, to answer here (or by voice). */
export function CallRequest({ call, className }: { call: LiveCall; className?: string }) {
  if (call.request === null) return null;
  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 rounded-xl border bg-card p-3 text-sm shadow-sm',
        className,
      )}
    >
      <p className="flex items-start gap-2">
        <ShieldQuestion aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-warning" />
        <span className="min-w-0 flex-1">{call.request.label}</span>
      </p>
      <div className="flex justify-end gap-1.5">
        <button
          className="flex h-control-sm items-center rounded-md px-3 text-muted-foreground text-xs hover:bg-accent hover:text-foreground"
          onClick={() => call.answer(false)}
          type="button"
        >
          Deny
        </button>
        <button
          className="flex h-control-sm items-center rounded-md bg-primary px-3 font-medium text-primary-foreground text-xs hover:bg-primary/90"
          onClick={() => call.answer(true)}
          type="button"
        >
          Approve
        </button>
      </div>
    </div>
  );
}

/** Mute and hang up, as round buttons. */
/**
 * The assistants the user can bring into the call (a group call with the one on the line, or the
 * group's), or into a new call with the one it reaches; none past a group's size.
 */
export function InviteMenu({ call, children }: { call: LiveCall; children: ReactNode }) {
  const inCall =
    call.group?.members.map((member) => member.id) ?? (call.speaker === null ? [] : [call.speaker]);
  const others = call.assistants.filter((entry) => !inCall.includes(entry.id));
  if (others.length === 0 || inCall.length === 0 || inCall.length >= MAX_GROUP_PERSONAS)
    return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DesktopDropdownContent align="center" side="top">
        <DesktopDropdownLabel>
          {call.status === 'idle' ? 'Call together with' : 'Bring into the call'}
        </DesktopDropdownLabel>
        {others.map((entry) => (
          <DesktopDropdownItem key={entry.id} onSelect={() => call.invite(entry.id)}>
            {entry.title}
          </DesktopDropdownItem>
        ))}
      </DesktopDropdownContent>
    </DropdownMenu>
  );
}

export function CallControls({
  call,
  size = 'md',
  labelled = false,
}: {
  call: LiveCall;
  size?: 'md' | 'sm';
  /** Big buttons, each with its name under it, in a narrow Live tab (a phone). */
  labelled?: boolean;
}) {
  const label = (text: string) =>
    labelled ? (
      <span aria-hidden="true" className="type-caption text-muted-foreground @lg:hidden">
        {text}
      </span>
    ) : null;
  const big = labelled ? '@max-lg:size-[3.75rem] @max-lg:[&_svg]:size-6' : '';
  const slot = labelled ? 'flex flex-col items-center gap-1.5' : 'contents';
  return (
    <div
      className={cn(
        'flex items-start',
        size === 'md' ? 'gap-3' : 'gap-1.5',
        labelled && '@max-lg:gap-6',
      )}
    >
      <span className={slot}>
        <button
          aria-label={call.muted ? 'Unmute microphone' : 'Mute microphone'}
          aria-pressed={call.muted}
          className={cn(
            roundButton,
            roundSize[size],
            big,
            call.muted ? 'bg-foreground text-background hover:bg-foreground/90' : roundNeutral,
          )}
          disabled={call.status === 'connecting'}
          onClick={call.toggleMute}
          title={call.muted ? 'Unmute' : 'Mute'}
          type="button"
        >
          {call.muted ? <MicOff /> : <Mic />}
        </button>
        {label(call.muted ? 'Unmute' : 'Mute')}
      </span>
      <InviteMenu call={call}>
        <span className={slot}>
          <button
            aria-label="Bring an assistant into the call"
            className={cn(roundButton, roundSize[size], big, roundNeutral)}
            disabled={call.status === 'connecting'}
            title="Bring an assistant into the call"
            type="button"
          >
            <UserPlus />
          </button>
          {label('Invite')}
        </span>
      </InviteMenu>
      <span className={slot}>
        <button
          aria-label="Hang up"
          className={cn(
            roundButton,
            roundSize[size],
            big,
            'bg-destructive text-white hover:bg-destructive/90',
          )}
          onClick={call.hangUp}
          title="Hang up"
          type="button"
        >
          <PhoneOff />
        </button>
        {label('End')}
      </span>
    </div>
  );
}

/** Where a step of the assistant's turn stands. */
function StepIcon({ state }: { state: TurnStep['state'] }) {
  if (state === 'running')
    return (
      <LoaderCircle
        aria-label="Running"
        className="size-3 shrink-0 text-muted-foreground motion-safe:animate-spin"
      />
    );
  if (state === 'waiting')
    return <ShieldQuestion aria-label="Waits on you" className="size-3 shrink-0 text-attention" />;
  if (state === 'failed')
    return <X aria-label="Failed" className="size-3 shrink-0 text-destructive" />;
  return <Check aria-label="Done" className="size-3 shrink-0 text-success" />;
}
